import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAgendamentoStepIndicator } from "../../../hooks/useAgendamentoStepIndicator";
import { useCadastrarLoteAgendamento } from "../../../hooks/queries/loteAgendamento/useCadastrarLoteAgendamento/useCadastrarLoteAgendamento";
import { useVincularDeliveryLoteAgendamento } from "../../../hooks/queries/loteAgendamento/useVincularDeliveryLoteAgendamento/useVincularDeliveryLoteAgendamento";
import { useConcessionariaBusca } from "../../../hooks/queries/cliente/useConcessionariaBusca/useConcessionariaBusca";
import { useDeliveryBusca } from "../../../hooks/queries/delivery/useDeliveryBusca/useDeliveryBusca";
import { useContextSite } from "../../../context/Context";
import { useUserPermissions } from "../../../hooks/useUserPermissions";
import { Tracking } from "../../../services/Tracking";
import { ISelectOptions } from "../../../types/inputs";

export type IBatchDeliveryModalidade = "domicilio" | "concessionaria";

export const useBatchDelivery = () => {
  const navigate = useNavigate();
  const stepIndicator = useAgendamentoStepIndicator();
  const { setIsLoad } = useContextSite();
  const { userStorageData } = useUserPermissions();

  const [modalidade, setModalidade] =
    useState<IBatchDeliveryModalidade>("domicilio");
  const [selectedCidade, setSelectedCidade] =
    useState<ISelectOptions | null>(null);
  const [cidadeError, setCidadeError] = useState<string>();
  const [selectedConcessionaria, setSelectedConcessionaria] =
    useState<ISelectOptions | null>(null);
  const [concessionariaError, setConcessionariaError] = useState<string>();

  const { buscarConcessionarias } = useConcessionariaBusca();
  const { buscarDeliveries } = useDeliveryBusca();
  const { mutate: cadastrarLoteAgendamento } = useCadastrarLoteAgendamento();
  const { mutate: vincularDeliveryLoteAgendamento } =
    useVincularDeliveryLoteAgendamento();

  const loadCidadeOptions = (cidade: string) =>
    buscarDeliveries(cidade)
      .then((data) =>
        data.content.map((item) => ({
          value: item.uuid,
          label: `${item.cidade}/${item.uf}`,
          element: item,
        })),
      )
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          Tracking.error(
            "mapa:agendamento-lote/delivery:buscar-cidades-falhou",
            { mensagem },
          );
          toast.error(mensagem);
          return [];
        },
      );

  const loadOptions = (nome: string) =>
    buscarConcessionarias(nome, selectedCidade?.element?.cidade)
      .then((data) =>
        data.content.map((item) => ({
          value: item.uuid,
          label: item.nome,
          element: item,
        })),
      )
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          Tracking.error(
            "mapa:agendamento-lote/delivery:buscar-concessionarias-falhou",
            { mensagem },
          );
          toast.error(mensagem);
          return [];
        },
      );

  function handleModalidadeChange(value: IBatchDeliveryModalidade) {
    setModalidade(value);
    setConcessionariaError(undefined);
    Tracking.userAction(
      value === "domicilio"
        ? "mapa:agendamento-lote/delivery:clique-card-domicilio"
        : "mapa:agendamento-lote/delivery:clique-card-concessionaria",
    );
  }

  function handleCidadeChange(value: ISelectOptions) {
    setSelectedCidade(value);
    setCidadeError(undefined);
    setSelectedConcessionaria(null);
    setConcessionariaError(undefined);
  }

  function handleSubmit() {
    if (!selectedCidade) {
      setCidadeError("Você precisa selecionar uma cidade");
      return;
    }

    if (modalidade === "concessionaria" && !selectedConcessionaria) {
      setConcessionariaError("Você precisa selecionar uma concessionária");
      return;
    }

    setIsLoad(true);
    cadastrarLoteAgendamento(
      {
        tipoAtendimento: "DOMICILIO",
        uuidDespachanteResponsavel: userStorageData?.uuidCliente ?? "",
      },
      {
        onSuccess: ({ data: loteAgendamento }) => {
          vincularDeliveryLoteAgendamento(
            {
              uuid: loteAgendamento.uuid,
              concessionaria: modalidade === "concessionaria",
              uuidConcessionaria: selectedConcessionaria?.value,
              uuidDelivery: selectedCidade.value,
            },
            {
              onSuccess: () => {
                navigate(`/agendamento-lote/${loteAgendamento.uuid}/servicos`);
              },
              onError: ({
                response: {
                  data: { mensagem },
                },
              }: any) => {
                Tracking.error(
                  "mapa:agendamento-lote/delivery:vincular-delivery-falhou",
                  { mensagem },
                );
              },
              onSettled: () => setIsLoad(false),
            },
          );
        },
        onError: ({
          response: {
            data: { mensagem },
          },
        }: any) => {
          Tracking.error(
            "mapa:agendamento-lote/delivery:criar-lote-agendamento-falhou",
            { mensagem },
          );
          setIsLoad(false);
        },
      },
    );
  }

  return {
    stepIndicator,
    modalidade,
    handleModalidadeChange,
    loadCidadeOptions,
    selectedCidade,
    handleCidadeChange,
    cidadeError,
    loadOptions,
    selectedConcessionaria,
    setSelectedConcessionaria,
    concessionariaError,
    handleSubmit,
  };
};
